import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';

import useAlert from 'hooks/useAlert';

import axios, { openAxios } from 'services/axios';
import { OPEN, PAYMENT } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

import { tapPaymentToken } from './TapIframe';
import getTapPaymentUserPhone from './getTapPaymentUserPhone';

let popup;

const CompleteSubscriptionTapPaymentButton = ({
	selectedPackage,
	selectedSavedCard,
	planType,
	onPaymentResult,
	isPayTR,
	...props
}) => {
	const dispatch = useDispatch();
	const formMethods = useFormContext();
	const { loading } = useSelector((state) => state?.layout);
	const subscriptionPromoCodeCheck = useSelector((state) => state.subscriptionAccount.subscriptionPromoCodeCheck);
	const { showError } = useAlert();
	const { handleSubmit, watch } = formMethods;
	const [paymentResultChecking, setPaymentResultChecking] = useState(false);
	const userData = useSelector((state) => state?.user);

	const checkSubscription = async (id) => {
		try {
			const { data } = await axios.post(PAYMENT.CHECK_SUBSCRIPTION, {
				paymentID: id,
				newPackageID: selectedPackage.id
			});
			if (data.status === 'pending') {
				const timeout = setTimeout(() => {
					clearTimeout(timeout);
					checkSubscription(id);
				}, 3 * 1000);
			} else {
				onPaymentResult(data);
				dispatch(setLoading(false));
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const checkFirstSubscription = async (id) => {
		try {
			await openAxios.post(OPEN.POST_SUBSCRIPTION_FIRST_PAYMENT_RESULT, {
				id,
				threeDSecure: true,
				keepStatus: true
			});
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	useEffect(() => {
		const handleMessage = (message) => {
			if (message.data.event !== 'checkPaymentResult') return;

			setPaymentResultChecking(true);
			popup.close();

			setTimeout(async () => {
				await checkFirstSubscription(message.data?.paymentID);
				await checkSubscription(message.data?.paymentID);
			}, 5 * 1000); // wait for 5 second to make sure the webhook is triggered
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	useEffect(() => {
		if (!loading || paymentResultChecking) return;
		const interval = setInterval(() => {
			if (popup?.closed) {
				dispatch(setLoading(false));
			}
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [loading, paymentResultChecking]);

	const sendPaymentRequest = async () => {
		dispatch(setLoading(true));

		const userPhone = getTapPaymentUserPhone();

		await axios
			.post(PAYMENT.CREATE_SUBSCRIPTION, {
				newPackageID: selectedPackage.id,
				countryCode: userPhone.countryCode,
				phone: userPhone.phone,
				paymentType: planType.toUpperCase(),
				token: tapPaymentToken,
				clientCardID: selectedSavedCard?.card ? Number(selectedSavedCard.card) : undefined,
				promoCode: subscriptionPromoCodeCheck
			})
			.then((res) => {
				const { data, status } = res;
				if (status === 200 && data) {
					const { transactionUrl } = data;
					if (transactionUrl) {
						popup.location.href = transactionUrl;
					} else {
						popup.close();
						if (data.status === 'upgraded' || data.status === 'paid') {
							onPaymentResult({ status: 'paid' });
							dispatch(setLoading(false));
						} else {
							throw new Error(JSON.stringify(data));
						}
					}
					window.dataLayer.push({
						/* eslint-disable camelcase */
						event: 'subscription',
						first_name: userData?.firstName || '',
						last_name: userData?.lastName || '',
						phone: userData?.phone || '',
						email: userData?.email || '',
						plan: userData?.planName || '',
						country: userData?.localization?.label || '',
						userID: userData?.ID || '',
						subscribing_plan: selectedPackage?.planName || ''
						/* eslint-enable camelcase */
					});

					const eventName = {
						StarterPackage: 'scale_plan_subscribed',
						ScalePackage: 'scale_plan_subscribed'
					};

					if (eventName[selectedPackage?.planName]) {
						window.dataLayer.push({
							/* eslint-disable camelcase */
							event: eventName[selectedPackage?.planName],
							first_name: userData?.firstName || '',
							last_name: userData?.lastName || '',
							phone: userData?.phone || '',
							email: userData?.email || '',
							plan: userData?.planName || '',
							country: userData?.localization?.label || '',
							userID: userData?.ID || '',
							subscribing_plan: selectedPackage?.planName || ''
							/* eslint-enable camelcase */
						});
					}
				} else {
					popup.close();
					showError();
					AppLogger.error(res);
					dispatch(setLoading(false));
				}
			})
			.catch((error) => {
				popup.close();
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
				dispatch(setLoading(false));
			});
	};

	const payTRSubscriptionPayment = async () => {
		dispatch(setLoading(true));
		const userPhone = getTapPaymentUserPhone();
		popup = window.open('/payment-redirect-tr.html', '_blank');
		let paymentPayload;
		if (selectedSavedCard?.card) {
			paymentPayload = {
				clientCardID: selectedSavedCard?.card ? Number(selectedSavedCard.card) : undefined
			};
		} else {
			paymentPayload = {
				ccOwner: watch('cardHolderName'),
				cardNumber: watch('cardNumber')?.replace?.(/\s/g, ''),
				expiryMonth: watch('cardExpiry')?.split('/')[0].toString(),
				expiryYear: watch('cardExpiry')?.split('/')[1].toString(),
				cvv: watch('cvv')
			};
		}

		await axios
			.post(PAYMENT.CREATE_SUBSCRIPTION, {
				newPackageID: selectedPackage.id,
				countryCode: userPhone.countryCode,
				phone: userPhone.phone,
				paymentType: planType.toUpperCase(),
				promoCode: subscriptionPromoCodeCheck,
				...paymentPayload
			})
			.then((res) => {
				const { data, status } = res;
				if (status === 200 && data) {
					const { transactionUrl, status } = data;
					const transactionResponse = JSON?.parse?.(transactionUrl || '{}');

					if (transactionResponse?.code === 200 && transactionResponse?.response) {
						popup.document.write(transactionResponse.response);
						popup.document?.body?.onload?.();
					} else {
						popup.close();
						if (status === 'upgraded' || status === 'paid') {
							onPaymentResult({ status: 'paid' });
							dispatch(setLoading(false));
						}
					}
					if (transactionResponse?.code === 200) {
						setPaymentResultChecking(true);
						checkSubscription(data.paymentID);
					}
					window.dataLayer.push({
						/* eslint-disable camelcase */
						event: 'subscription',
						first_name: userData?.firstName || '',
						last_name: userData?.lastName || '',
						phone: userData?.phone || '',
						email: userData?.email || '',
						plan: userData?.planName || '',
						country: userData?.localization?.label || '',
						userID: userData?.ID || '',
						subscribing_plan: selectedPackage?.planName || ''
						/* eslint-enable camelcase */
					});
				} else {
					popup.close();
					showError();
					AppLogger.error(res);
					dispatch(setLoading(false));
				}
			})
			.catch((error) => {
				popup.close();
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
				dispatch(setLoading(false));
			});
	};

	const completeCreditPayment = async () => {
		if (isPayTR) {
			return payTRSubscriptionPayment();
		}
		popup = window.open('/payment-redirect-en.html', '_blank');
		if (selectedSavedCard) {
			await sendPaymentRequest();
		} else {
			window.CardSDK.tokenize();
		}
	};

	const handleTokenCreate = () => {
		sendPaymentRequest();
	};

	const handleTokenError = () => {
		popup.close();
		dispatch(setLoading(false));
		showError();
	};

	useEffect(() => {
		window.addEventListener('tapPaymentTokenCreated', handleTokenCreate);
		window.addEventListener('tapPaymentTokenError', handleTokenError);

		return () => {
			window.removeEventListener('tapPaymentTokenCreated', handleTokenCreate);
			window.removeEventListener('tapPaymentTokenError', handleTokenError);
		};
	}, []);

	return (
		<Button
			label="completeSubscription"
			loading={loading}
			onClick={handleSubmit(completeCreditPayment, AppLogger.error)}
			{...props}
		/>
	);
};

export default CompleteSubscriptionTapPaymentButton;
